<template>
    <div>
        <div class="grid-fitted" @click.prevent="toggleFilter" :class="{'cursor-pointer': isReady}">
            <div class="grid-item">
                <v-icon :icon="filterIcon" class="inline-s" :class="{'is-spinning': !isReady}"/>
                <span class="text-muted" v-if="hasFilters && isReady">You have {{countLabel}} being applied</span>
                <span class="text-muted" v-else-if="!hasFilters && isReady">No filters are being applied</span>
                <span class="text-muted" v-else-if="!isReady">Loading filters...</span>
            </div>
            <div class="no-flex" v-show="isReady">
                <v-icon icon="caret-right" :class="{'is-rotating-right': isOpen}"/>
            </div>
        </div>
        <div class="margin-top-m" v-show="isOpen && isReady">
            <query
                @ready="dispatchReady"
                @update="update"
                @validation="queryValidated"
                :query="filters"
                :filter-set="filterSet"/>
            <div class="divider divider-tile-content-m"></div>
            <r-button
                class="inline-s"
                size="small"
                :disabled="!canSave || isReadOnly || isTemporary"
                @click="save">Save
            </r-button>
            <r-button
                class="inline-m"
                size="small"
                :disabled="!canSave"
                @click="showSaveAsModal(true)">Save Segment As...
            </r-button>
            <span v-if="!canSave" class="text-muted">Created by another user.</span>
        </div>
        <v-modal v-if="isSaveAsModalVisible" title="Save Segment As" @close="showSaveAsModal(false)" size="small">
            <div slot="content">
                <r-input
                    :validate="$v.segmentCopyName"
                    v-model="segmentCopyName"
                    help-text="Provide a name for the new segment."
                    label="Name"/>
            </div>
            <template slot="actions">
                <r-button @click="saveAs" type="primary">Save</r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import {mapGetters, mapActions, mapState} from 'vuex';
    import {vIcon} from '@/components/form';
    import pluralize from '@/common/helpers/pluralize';
    import vModal from '@/components/modal/v-modal';
    import {required} from 'vuelidate/lib/validators';
    import debounce from '@/common/helpers/debounce';

    export default {
        name: 'segment-filter',
        components: {
            Query: () => import('@/components/query2/query2'),
            vIcon,
            vModal,
        },
        props: {
            query: Array,
            filterSet: Object,
        },
        watch: {
            query(query) {
                this.count = this.getCountFromQuery(query);
            },
        },
        computed: {
            ...mapState('segments', ['activeFilters']),
            ...mapGetters({
                activeSegment: 'segments/activeSegment',
                isImpersonating: 'user/isImpersonating',
            }),
            countLabel() {
                return `${this.count} ${pluralize('filter', this.count)}`;
            },
            canSave() {
                if (this.activeSegment) {
                    if (this.isImpersonating) {
                        return true;
                    }
                    return (this.$store.state.user.memberships[0].user.id === this.activeSegment.owner.id)
                        && this.$auth.gridSegments.add.isAllowed;
                }
                return false;
            },
            isReadOnly() {
                return this.activeSegment.isReadOnly;
            },
            hasFilters() {
                return this.count > 0;
            },
            filterIcon() {
                return this.isReady ? 'filter' : 'loading';
            },
            isTemporary() {
                return this.activeSegment.isTemporary;
            },
        },
        data() {
            return {
                isOpen: false,
                isReady: false,
                count: this.query ? this.query.length : 0,
                filters: this.query || [],
                isSaveAsModalVisible: false,
                segmentCopyName: '',
                hasInvalidQuery: false,
                debouncedUpdate: debounce(query => this.$emit('update', query), 700, false),
            };
        },
        validations: {
            segmentCopyName: {required},
        },
        methods: {
            ...mapActions({
                updateSegment: 'segments/update',
                addSegment: 'segments/add',
                updateActiveFilters: 'segments/updateActiveFilters',
            }),
            toggleFilter() {
                if (this.isReady) {
                    this.isOpen = !this.isOpen;
                }
            },
            showSaveAsModal(status) {
                if (status) {
                    this.segmentCopyName = `Copy of ${this.activeSegment.data.name}`;
                }

                this.isSaveAsModalVisible = status;
            },
            update(query) {
                this.count = Array.isArray(query) ? query.length : 0;
                this.updateActiveFilters(query);
                this.debouncedUpdate(query);
            },
            async save() {
                const segment = this.activeSegment.cloneSelf();
                segment.data.filters = this.activeFilters;
                try {
                    await this.updateSegment(segment, true);
                    this.$toast.positive(`Segment ${this.activeSegment.data.name} was updated`);
                } catch (error) {
                    this.$toast.fromError(error);
                }
            },
            async saveAs() {
                this.$v.$touch();
                if (!this.$v.$error) {
                    const added = this.activeSegment.cloneSelf();
                    added.data.filters = this.activeFilters;
                    added.data.name = this.segmentCopyName;
                    added.id = null;
                    this.$parent.$emit('new-segment-save', added);
                }
            },
            getFilters() {
                return this.filters;
            },
            queryValidated({hasInvalidItems}) {
                this.hasInvalidQuery = hasInvalidItems;
            },
            dispatchReady($event) {
                this.$emit('ready', $event);
                this.isReady = true;
            },
            getCountFromQuery(data) {
                return data ? data.length : 0;
            },
        },
    };
</script>

<style scoped>

</style>
